import { useState } from "react";
import { Trans, useLingui } from "@lingui/macro";
import API from "@app/services/api";

/**
 * The official Pro Santé Connect button.
 *
 * The ANS publishes this button and its graphic charter, and **using any other button is not
 * permitted** — which is why nothing here draws it. The asset comes from the ANS kit and lives at
 * `public/logos/pro-sante-connect.svg`; this component only places it and enforces what the charter
 * asks of the placement:
 *
 *  - 220px is the standard width, and the height follows (`h-auto`) so the ratio is never distorted;
 *  - 20px of clear space around the button, which is what `p-5` is;
 *  - no text immediately before or after it. The wording « s'identifier avec PRO SANTÉ CONNECT » is
 *    part of the asset, in Futura PT, and is not ours to re-set in the app's own font.
 *
 * The asset is the JPG of the official kit, and it is a JPG for one reason: the kit itself sits
 * behind the ANS portal, which refuses automated download, while the ANS serves this same file from
 * its own sandbox demo service. The kit also ships SVG, PNG, EPS and AI — prefer the SVG when
 * somebody with portal access can fetch it, and point this constant at it. 1153x295 is roughly five
 * times the displayed width, so it is sharp at any density; the only thing it costs is an opaque
 * white background, which is invisible against the near-white surfaces the button sits on.
 *
 * The fallback is not a second button. If the asset is missing — an environment where Pro Santé
 * Connect was switched on before the kit was dropped in — a broken image would be the worst of the
 * available outcomes, so a plain text button takes over. It deliberately looks like nothing
 * official: an approximation of the real one would be exactly what the charter forbids.
 */

const PSC_LOGO = "/logos/pro-sante-connect.jpg";
/** The charter's standard width. Resizing is allowed, distorting is not. */
const PSC_WIDTH = 220;

interface PscLoginButtonProps {
  /** "login" signs in; "link" binds the card to the account already signed in. */
  intent?: "login" | "link";
  /** Rendered under the button — never beside it (see the charter note above). */
  hint?: React.ReactNode;
}

export default function PscLoginButton({
  intent = "login",
  hint,
}: PscLoginButtonProps) {
  const { t } = useLingui();
  const [assetMissing, setAssetMissing] = useState(false);

  // A top-level navigation on purpose: the ANS does not load inside an XHR, and the transaction
  // cookie has to be set on the browser.
  const start = () => {
    window.location.href = API.getUrl("/user/psc/authorize", { intent });
  };

  return (
    <div className="flex flex-col items-center">
      <button
        type="button"
        onClick={start}
        aria-label={t`S'identifier avec Pro Santé Connect`}
        className={
          assetMissing
            ? "rounded-lg border border-primary/30 bg-white px-6 py-2.5 text-sm font-semibold text-primary transition-all hover:border-primary hover:bg-primary/5"
            : "rounded-lg p-5 transition-opacity hover:opacity-90"
        }
      >
        {assetMissing ? (
          <Trans>S'identifier avec Pro Santé Connect</Trans>
        ) : (
          <img
            src={PSC_LOGO}
            width={PSC_WIDTH}
            alt={t`S'identifier avec Pro Santé Connect`}
            className="h-auto"
            onError={() => setAssetMissing(true)}
          />
        )}
      </button>
      {hint}
    </div>
  );
}
